/**
 * Single source of truth for how a flow's lifecycle status is presented in the UI.
 *
 * A flow carries several independent status fields:
 *   - `syncStatus`       PENDING | SYNCING | SYNCED | FAILED   (ADR-0006 background sync to Meta)
 *   - `whatsappStatus`   DRAFT | PUBLISHED | DEPRECATED | BLOCKED | THROTTLED  (Meta's own status)
 *   - `status`           DRAFT | DEPLOYED | PUBLISHED | DEPRECATED            (our local lifecycle)
 *   - `validationStatus` UNKNOWN | VALID | INVALID
 *
 * `getFlowStatusDisplay` collapses them into the one badge a user should see. Every flow now
 * always syncs to the WABA (ADR-0006), so there is no "local only" state, a flow that has not
 * reached Meta yet is simply *syncing* (or its sync *failed*), not a permanent local draft.
 *
 * Returns `{ key, label, color, variant, description, live }`:
 *   - `key`     stable identifier for mapping to an icon in the consumer
 *   - `color`   MUI chip color ('success' | 'primary' | 'warning' | 'error' | 'default' | 'info')
 *   - `variant` 'filled' for live/terminal states, 'outlined' for transient/draft states
 *   - `live`    true when the flow is (or was) live on WhatsApp and customers could reach it
 */
export function getFlowStatusDisplay(flow) {
  const meta = String(flow?.whatsappStatus || '').toUpperCase();
  const local = String(flow?.status || '').toUpperCase();
  const sync = String(flow?.syncStatus || '').toUpperCase();

  // Meta can independently flag a live flow as blocked/throttled for quality or policy reasons,
  // these outrank our local lifecycle because they describe what WhatsApp is actually doing.
  if (meta === 'BLOCKED') {
    return {
      key: 'blocked',
      label: 'Blocked',
      color: 'error',
      variant: 'filled',
      live: true,
      description: 'WhatsApp has blocked this flow. It cannot be used until the issue is resolved.',
    };
  }
  if (meta === 'THROTTLED') {
    return {
      key: 'throttled',
      label: 'Throttled',
      color: 'warning',
      variant: 'filled',
      live: true,
      description: 'WhatsApp is throttling this flow, usually due to a low quality rating.',
    };
  }

  if (local === 'DEPRECATED' || meta === 'DEPRECATED') {
    return {
      key: 'deprecated',
      label: 'Deprecated',
      color: 'default',
      variant: 'filled',
      live: false,
      description: 'Retired on WhatsApp. It can no longer be sent, but existing sessions still work.',
    };
  }

  if (local === 'PUBLISHED' || meta === 'PUBLISHED') {
    return {
      key: 'published',
      label: 'Published',
      color: 'success',
      variant: 'filled',
      live: true,
      description: 'Live on WhatsApp and can be sent to customers.',
    };
  }

  // Not a published/deprecated/blocked/throttled flow, i.e. a draft. Its badge is driven by the
  // sync state of the latest content. The authoritative "is it on Meta" signal is `syncStatus`,
  // NOT `whatsappFlowId`: a flow can be SYNCED without a recorded id (older/edge rows), which is
  // why the builder (which keys on syncStatus) showed "Synced" while the list showed "Syncing".
  // There is no "local only" state, we always sync.
  if (sync === 'FAILED') {
    return {
      key: 'sync-failed',
      label: 'Sync failed',
      color: 'error',
      variant: 'outlined',
      live: false,
      description: flow?.syncError
        ? `Could not sync to WhatsApp: ${flow.syncError}`
        : 'Could not sync to WhatsApp. It will be retried.',
    };
  }
  if (sync === 'SYNCED') {
    return {
      key: 'draft',
      label: 'Draft',
      color: 'info',
      variant: 'outlined',
      live: false,
      description: 'Synced to WhatsApp and ready to publish. Not yet live to customers.',
    };
  }
  // PENDING / SYNCING / unknown, the latest content is still on its way to Meta.
  return {
    key: 'syncing',
    label: 'Syncing…',
    color: 'default',
    variant: 'outlined',
    live: false,
    description: 'Saving to WhatsApp and validating. This usually takes a few seconds.',
  };
}

/**
 * Number of validation errors WhatsApp reported for a flow, read straight from the persisted
 * `validationErrors` blob the flow already carries (refreshed asynchronously by the background
 * monitor, never fetched from Meta on view). Returns 0 when there are none or the flow hasn't
 * been validated yet.
 */
export function getFlowErrorCount(flow) {
  const errors = flow?.validationErrors?.errors;
  return Array.isArray(errors) ? errors.length : 0;
}

/**
 * Whether WhatsApp considers the flow invalid. INVALID can be set even when the detailed error
 * list hasn't been (re)populated yet, so callers should treat "invalid with 0 listed" as "≥1".
 */
export function isFlowInvalid(flow) {
  return String(flow?.validationStatus || '').toUpperCase() === 'INVALID';
}

/**
 * First few WhatsApp error messages, for a tooltip. Empty array when none are listed.
 */
export function getFlowErrorMessages(flow, limit = 5) {
  const errors = flow?.validationErrors?.errors;
  if (!Array.isArray(errors)) {
    return [];
  }
  return errors
    .slice(0, limit)
    .map((e) => e?.message || e?.error || 'Validation error');
}

export default getFlowStatusDisplay;
